import * as Updates from 'expo-updates'
import { useEffect, useRef } from 'react'
import { AppState, type AppStateStatus, Platform } from 'react-native'
import { errorToString } from '@/lib/errors'
import { log } from '@/lib/logger'
import { notify } from '@/lib/notice'
import { pb } from '@/lib/pocketbase'
import type { UserMetadata } from '@/lib/types'

function isBetaTester() {
    const record = pb.authStore.record as { metadata?: UserMetadata } | null
    return !!record?.metadata?.isBetaTester
}

function syncUpdateChannel() {
    const channel = isBetaTester() ? 'preview' : 'production'
    log.debug(`Setting update channel to ${channel}`)
    Updates.setUpdateRequestHeadersOverride({
        'expo-channel-name': channel,
    })
}

async function checkAndApplyUpdate() {
    syncUpdateChannel()

    const check = await Updates.checkForUpdateAsync()
    if (!check.isAvailable) {
        log.debug('No update available')
        return
    }

    log.info('Update available, downloading...')
    notify.info('Updating app…')
    const result = await Updates.fetchUpdateAsync()

    if (result.isNew) {
        log.info('Update downloaded, reloading app')
        await Updates.reloadAsync()
    }
}

export function useAppUpdates() {
    const appStateRef = useRef<AppStateStatus>(AppState.currentState)

    useEffect(() => {
        if (__DEV__ || Platform.OS === 'web') {
            return
        }

        const timeoutId = setTimeout(async () => {
            try {
                await checkAndApplyUpdate()
            } catch (e) {
                log.errorWithoutSentry('update-check-on-launch:', errorToString(e))
            }
        }, 3000)

        const subscription = AppState.addEventListener(
            'change',
            async (nextAppState: AppStateStatus) => {
                const wasBackground = appStateRef.current.match(/inactive|background/)
                appStateRef.current = nextAppState

                if (wasBackground && nextAppState === 'active') {
                    try {
                        await checkAndApplyUpdate()
                    } catch (e) {
                        log.errorWithoutSentry('update-check-on-foreground:', errorToString(e))
                    }
                }
            }
        )

        return () => {
            clearTimeout(timeoutId)
            subscription.remove()
        }
    }, [])
}
